import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, MessageCircleHeart, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import Marquee from "@/components/Marquee";
import { Reveal, MaskedLines } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Candle() {
  return (
    <div className="relative w-10 h-16 mx-auto">
      <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-amber-400/40 blur-md" />
      <div className="flame absolute top-0 left-1/2 -translate-x-1/2 w-3.5 h-6 bg-gradient-to-t from-amber-500 via-amber-400 to-yellow-200"
        style={{ borderRadius: "50% 50% 50% 50% / 62% 62% 38% 38%" }} />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-5 h-11 rounded-t-md bg-gradient-to-b from-[#F3E8FF] to-[#DDD0EE] border border-purple-200" />
    </div>
  );
}

export default function Wall() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ name: "", kind: "candle", message: "" });
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/dedications`);
      setItems(await res.json());
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/dedications`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(typeof data.detail === "string" ? data.detail : "Please try again.");
      }
      toast.success(form.kind === "candle" ? "Thank you — your candle has been received" : "Thank you — your message has been received", {
        description: "A staff member will review it shortly, and it will appear on the wall once approved.",
        duration: 7000,
      });
      setForm({ name: "", kind: "candle", message: "" });
    } catch (err) {
      toast.error("Something went wrong", { description: err.message });
    } finally {
      setSending(false);
    }
  };

  const candleCount = (items || []).filter((d) => d.kind === "candle").length;
  const inputCls = "w-full rounded-xl border border-purple-200 bg-white px-4 py-3 text-sm text-[#1E1B4B] focus:outline-none focus:ring-2 focus:ring-[#5B21B6] focus:border-transparent transition-shadow";

  return (
    <div data-testid="wall-page">
      <section className="relative overflow-hidden bg-[#2E1065] grain">
        <div className="absolute top-10 left-1/4 w-72 h-72 rounded-full bg-amber-500/10 blur-3xl" />
        <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-16 pb-20 lg:pt-24 text-center relative">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-amber-400 mb-4">The Butterfly Dedication Wall</p>
          <MaskedLines
            className="font-display font-black text-[#FAF8F5] text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight"
            lines={["Light a candle.", "Leave a little love."]}
          />
          <Reveal delay={0.4}>
            <p className="mt-7 text-base sm:text-lg text-purple-200 leading-relaxed max-w-2xl mx-auto" data-testid="wall-intro">
              In memory of Jaliyah Ikja Mason (2010 — 2026). Light a virtual candle or leave a message — every
              flame here keeps her light shining over every home that carries her name.
            </p>
            <p className="mt-5 inline-flex items-center gap-2 bg-white/10 border border-amber-400/40 rounded-full px-5 py-2 text-sm font-bold text-amber-300" data-testid="candle-count">
              <Flame size={15} /> {candleCount} {candleCount === 1 ? "candle" : "candles"} lit
            </p>
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-24 grid lg:grid-cols-5 gap-12">
        <Reveal className="lg:col-span-2">
          <form onSubmit={submit} data-testid="dedication-form" className="bg-white rounded-3xl border border-purple-100 shadow-xl p-7 sm:p-9 space-y-5 lg:sticky lg:top-28">
            <div className="flex justify-center"><Candle /></div>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="kind-candle-btn"
                onClick={() => setForm({ ...form, kind: "candle" })}
                className={`rounded-2xl border-2 px-4 py-3.5 text-sm font-bold flex items-center justify-center gap-2 transition-colors ${
                  form.kind === "candle" ? "border-amber-400 bg-[#FEF3C7] text-[#B45309]" : "border-purple-100 text-slate-500 hover:border-purple-200"
                }`}
              >
                <Flame size={16} /> Light a Candle
              </button>
              <button
                type="button"
                data-testid="kind-message-btn"
                onClick={() => setForm({ ...form, kind: "message" })}
                className={`rounded-2xl border-2 px-4 py-3.5 text-sm font-bold flex items-center justify-center gap-2 transition-colors ${
                  form.kind === "message" ? "border-[#5B21B6] bg-[#F3E8FF] text-[#5B21B6]" : "border-purple-100 text-slate-500 hover:border-purple-200"
                }`}
              >
                <MessageCircleHeart size={16} /> Leave a Message
              </button>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#2E1065] mb-2">Your Name (optional)</label>
              <input data-testid="dedication-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Anonymous" maxLength={80} />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#2E1065] mb-2">
                {form.kind === "candle" ? "A few words with your candle (optional)" : "Your Message *"}
              </label>
              <textarea
                data-testid="dedication-message-input"
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required={form.kind === "message"}
                className={inputCls}
                placeholder="A memory, a prayer, a few words of love..."
                maxLength={600}
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="dedication-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#5B21B6] hover:bg-[#2E1065] disabled:opacity-60 text-white font-bold px-8 py-4 rounded-full shadow-lg shadow-purple-500/25 transition-colors duration-300"
            >
              {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              {sending ? "Placing..." : form.kind === "candle" ? "Light the Candle" : "Place on the Wall"}
            </button>
            <p className="text-[11px] text-slate-400 text-center leading-relaxed" data-testid="moderation-notice">
              To keep this space gentle and safe, every candle and message is reviewed by our staff before it appears publicly.
            </p>
          </form>
        </Reveal>

        <div className="lg:col-span-3">
          {items === null ? (
            <div className="flex justify-center py-20"><Loader2 size={32} className="animate-spin text-[#5B21B6]" /></div>
          ) : items.length === 0 ? (
            <Reveal>
              <div data-testid="wall-empty-state" className="text-center py-20 bg-white rounded-3xl border border-purple-100">
                <Flame size={36} className="mx-auto text-amber-400 mb-4" />
                <p className="font-display font-bold text-xl text-[#2E1065]">Be the first light on the wall</p>
                <p className="text-sm text-slate-500 mt-2 max-w-sm mx-auto">
                  Every candle and message here honors Jaliyah's memory and encourages the families who walk through our doors.
                </p>
              </div>
            </Reveal>
          ) : (
            <div className="grid sm:grid-cols-2 gap-5" data-testid="dedication-wall-grid">
              <AnimatePresence>
                {items.map((d) => (
                  <motion.div
                    key={d.id}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    data-testid={`dedication-card-${d.id}`}
                    className={`rounded-3xl p-6 border shadow-sm ${
                      d.kind === "candle"
                        ? "bg-gradient-to-b from-[#2E1065] to-[#3B1580] border-amber-400/40 text-[#FAF8F5]"
                        : "bg-white border-purple-100"
                    }`}
                  >
                    {d.kind === "candle" && <div className="mb-3"><Candle /></div>}
                    {d.message && (
                      <p className={`font-display italic text-base leading-relaxed ${d.kind === "candle" ? "text-purple-100" : "text-[#2E1065]"}`}>
                        "{d.message}"
                      </p>
                    )}
                    <div className={`mt-4 flex items-center gap-2 text-xs font-bold uppercase tracking-wider ${d.kind === "candle" ? "text-amber-300" : "text-[#B45309]"}`}>
                      {d.kind === "candle" ? <Flame size={13} /> : <MessageCircleHeart size={13} />}
                      {d.kind === "candle" ? "Candle lit by" : "Message from"} {d.name}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
